import React, { useMemo, useState } from 'react';
import { useUtilizationContext, getSundayIso, formatWeekDisplay } from './UtilizationDataContext';

const S = {
  container: {
    maxWidth: 720,
    margin: '48px auto',
    padding: '32px 36px',
    background: '#263238',
    borderRadius: 10,
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    color: '#eceff1',
    fontFamily: 'inherit',
  },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#e3f2fd' },
  subtitle: { margin: '0 0 28px', fontSize: 13, color: '#90caf9' },
  section: { marginBottom: 24 },
  label: { display: 'block', fontWeight: 600, fontSize: 13, color: '#b0bec5', marginBottom: 6 },
  weekRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  weekDisplay: {
    padding: '7px 14px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 6,
    color: '#e3f2fd',
    fontSize: 14,
    minWidth: 260,
    textAlign: 'center',
  },
  weekBtn: {
    padding: '7px 12px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 6,
    color: '#e3f2fd',
    cursor: 'pointer',
    fontSize: 14,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 6,
    color: '#eceff1',
    fontSize: 13,
    boxSizing: 'border-box',
  },
  searchRow: { display: 'flex', gap: 8, marginBottom: 6 },
  searchInput: {
    flex: 1,
    padding: '7px 10px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 6,
    color: '#eceff1',
    fontSize: 13,
  },
  checkList: {
    maxHeight: 180,
    overflowY: 'auto',
    border: '1px solid #455a64',
    borderRadius: 6,
    background: '#1e2e36',
    padding: '4px 0',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 12px',
    cursor: 'pointer',
    fontSize: 13,
    borderBottom: '1px solid #263238',
  },
  submitBtn: {
    marginTop: 28,
    width: '100%',
    padding: '12px',
    background: '#1565c0',
    border: '2px solid #0d47a1',
    borderRadius: 8,
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    letterSpacing: 0.3,
  },
  submitBtnDisabled: {
    background: '#455a64',
    border: '2px solid #37474f',
    cursor: 'not-allowed',
  },
  error: { color: '#ef5350', fontSize: 13, marginTop: 6 },
  hint: { color: '#78909c', fontSize: 12, marginTop: 4 },
};

function extractRoleId(role) {
  const v = role?.id ?? role?.workRoleId ?? role?.identifier ?? role?.value?.id;
  return v != null ? (typeof v === 'object' ? v.id ?? v.identifier : v) : null;
}

function extractRoleLabel(role) {
  return (
    role?.name?.value || role?.name ||
    role?.description ||
    role?.workRoleName ||
    (extractRoleId(role) != null ? String(extractRoleId(role)) : '(unnamed)')
  );
}

function extractUserId(u) {
  const v = u?.id ?? u?.identifier ?? u?.member?.identifier;
  return v != null ? (typeof v === 'object' ? v.id ?? v.identifier : v) : null;
}

function extractUserLabel(u) {
  return u?.name || u?.fullName || u?.identifier || String(extractUserId(u) ?? '');
}

export default function UtilizationPreFilter() {
  const {
    tokenLoading, tokenError,
    workRoles, workRolesLoading, workRolesError,
    selectedRoleIds, setSelectedRoleIds,
    users, usersLoading,
    selectedUserIds, setSelectedUserIds,
    weekStartIso, setWeekStartIso,
    projectFilter, setProjectFilter,
    commit,
  } = useUtilizationContext();

  const [roleSearch, setRoleSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [rolesOpen, setRolesOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);

  const metaReady = !tokenLoading && !workRolesLoading;

  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return workRoles;
    return workRoles.filter(r => extractRoleLabel(r).toLowerCase().includes(q));
  }, [workRoles, roleSearch]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => extractUserLabel(u).toLowerCase().includes(q));
  }, [users, userSearch]);

  function toggleRole(id) {
    setSelectedRoleIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    // Clear user selection when roles change
    setSelectedUserIds([]);
  }

  function toggleUser(id) {
    setSelectedUserIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  function shiftWeek(delta) {
    const d = new Date(weekStartIso + 'T00:00:00');
    d.setDate(d.getDate() + delta * 7);
    setWeekStartIso(getSundayIso(d));
  }

  const canSubmit = metaReady && !tokenError;

  if (tokenLoading) {
    return (
      <div style={S.container}>
        <p style={{ color: '#90caf9' }}>Signing in…</p>
      </div>
    );
  }

  return (
    <div style={S.container}>
      <h2 style={S.title}>IS and Eng Look Ahead</h2>
      <p style={S.subtitle}>Select filters to load utilization data.</p>

      {tokenError && (
        <div style={S.error}>Authentication error: {tokenError.message}</div>
      )}

      {/* Week picker */}
      <div style={S.section}>
        <span style={S.label}>Week (Sunday – Saturday)</span>
        <div style={S.weekRow}>
          <button style={S.weekBtn} onClick={() => shiftWeek(-1)}>‹ Prev</button>
          <span style={S.weekDisplay}>{formatWeekDisplay(weekStartIso)}</span>
          <button style={S.weekBtn} onClick={() => shiftWeek(1)}>Next ›</button>
        </div>
      </div>

      {/* Work Role / Team filter (optional) */}
      <div style={S.section}>
        <button
          onClick={() => setRolesOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', marginBottom: rolesOpen ? 8 : 0 }}
        >
          <span style={{ ...S.label, margin: 0 }}>{rolesOpen ? '▾' : '▸'} Team / Work Role <span style={{ fontWeight: 400, color: '#78909c' }}>(optional)</span></span>
          {selectedRoleIds.length > 0 && !rolesOpen && (
            <span style={{ fontSize: 11, color: '#1565c0', background: '#1a3a4a', border: '1px solid #1565c0', borderRadius: 10, padding: '1px 8px', marginLeft: 4 }}>{selectedRoleIds.length} selected</span>
          )}
        </button>
        {rolesOpen && (
          <>
        {workRolesError && <div style={S.error}>Failed to load roles: {workRolesError.message}</div>}
        {workRolesLoading ? (
          <div style={{ color: '#90caf9', fontSize: 13 }}>Loading roles…</div>
        ) : (
          <>
            <div style={S.searchRow}>
              <input
                style={S.searchInput}
                placeholder="Search roles…"
                value={roleSearch}
                onChange={e => setRoleSearch(e.target.value)}
              />
              {selectedRoleIds.length > 0 && (
                <button
                  style={{ ...S.weekBtn, fontSize: 12 }}
                  onClick={() => { setSelectedRoleIds([]); setSelectedUserIds([]); }}
                >
                  Clear
                </button>
              )}
            </div>
            <div style={S.checkList}>
              {filteredRoles.length === 0 && (
                <div style={{ padding: '8px 12px', color: '#78909c', fontSize: 13 }}>No roles found</div>
              )}
              {filteredRoles.map(role => {
                const id = extractRoleId(role);
                const label = extractRoleLabel(role);
                const checked = selectedRoleIds.includes(id);
                return (
                  <label
                    key={id}
                    style={{ ...S.checkItem, background: checked ? '#1a3a4a' : 'transparent' }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(id)}
                      style={{ accentColor: '#1565c0' }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            {selectedRoleIds.length > 0 && (
              <div style={S.hint}>{selectedRoleIds.length} role(s) selected</div>
            )}
          </>
        )}
          </>
        )}
      </div>

      {/* Resource (member) filter (optional) */}
      <div style={S.section}>
        <button
          onClick={() => setUsersOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', marginBottom: usersOpen ? 8 : 0 }}
        >
          <span style={{ ...S.label, margin: 0 }}>{usersOpen ? '▾' : '▸'} Resources <span style={{ fontWeight: 400, color: '#78909c' }}>(optional)</span></span>
          {selectedUserIds.length > 0 && !usersOpen && (
            <span style={{ fontSize: 11, color: '#1565c0', background: '#1a3a4a', border: '1px solid #1565c0', borderRadius: 10, padding: '1px 8px', marginLeft: 4 }}>{selectedUserIds.length} selected</span>
          )}
        </button>
        {usersOpen && (
          <>
        {usersLoading ? (
          <div style={{ color: '#90caf9', fontSize: 13 }}>Loading resources…</div>
        ) : (
          <>
            <div style={S.searchRow}>
              <input
                style={S.searchInput}
                placeholder="Search resources…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              {selectedUserIds.length > 0 && (
                <button
                  style={{ ...S.weekBtn, fontSize: 12 }}
                  onClick={() => setSelectedUserIds([])}
                >
                  Clear
                </button>
              )}
            </div>
            <div style={S.checkList}>
              {filteredUsers.length === 0 && (
                <div style={{ padding: '8px 12px', color: '#78909c', fontSize: 13 }}>No resources found</div>
              )}
              {filteredUsers.map(u => {
                const id = extractUserId(u);
                const label = extractUserLabel(u);
                const checked = selectedUserIds.includes(id);
                return (
                  <label
                    key={id}
                    style={{ ...S.checkItem, background: checked ? '#1a3a4a' : 'transparent' }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUser(id)}
                      style={{ accentColor: '#1565c0' }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            {selectedUserIds.length > 0 && (
              <div style={S.hint}>{selectedUserIds.length} resource(s) selected</div>
            )}
          </>
        )}
          </>
        )}
      </div>

      {/* Project # filter (optional) */}
      <div style={S.section}>
        <span style={S.label}>Project # (optional – comma-separate multiple)</span>
        <input
          style={S.input}
          placeholder="e.g. 12345, 67890"
          value={projectFilter}
          onChange={e => setProjectFilter(e.target.value)}
        />
        <div style={S.hint}>Filters to only assignments on matching project numbers.</div>
      </div>

      <button
        style={{ ...S.submitBtn, ...(canSubmit ? {} : S.submitBtnDisabled) }}
        disabled={!canSubmit}
        onClick={commit}
      >
        {workRolesLoading ? 'Loading…' : 'Load Utilization'}
      </button>
    </div>
  );
}
